/**
 * WindowContainer - Main container that displays the active window with its widgets
 */

import React, { useState, useRef, useEffect } from 'react';
import { Plus, Trash2, Edit2, Pencil, Check } from 'lucide-react';
import { WidgetGrid } from '../widgets/WidgetGrid';
import { useWindows } from '../../contexts/WindowsContext';
import { AddWidgetModal } from './AddWidgetModal';
import { JournalView } from '../JournalView';
import { FocusView } from '../FocusView';
import { AllTasksView } from '../AllTasksView';
import { ActivityView } from '../ActivityView';

export function WindowContainer() {
  const { activeWindow, isJournalActive, isFocusActive, isAllTasksActive, isActivityActive, deleteWindow, updateWindow, isEditing, setEditing } = useWindows();
  const [showAddWidget, setShowAddWidget] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  if (isJournalActive) {
    return <JournalView />;
  }

  if (isFocusActive) {
    return <FocusView />;
  }

  if (isAllTasksActive) {
    return <AllTasksView />;
  }

  if (isActivityActive) {
    return <ActivityView />;
  }

  if (!activeWindow) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-400">
        <p className="text-lg mb-2">No window selected</p>
        <p className="text-sm">Create a new window to get started</p>
      </div>
    );
  }

  const handleStartRename = () => {
    setEditName(activeWindow.name);
    setIsEditingName(true);
  };

  const handleSaveRename = () => {
    if (editName.trim() && editName !== activeWindow.name) {
      updateWindow(activeWindow.id, { name: editName.trim() });
    }
    setIsEditingName(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveRename();
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Window header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center gap-2">
          {isEditingName ? (
            <input
              ref={nameInputRef}
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onBlur={handleSaveRename}
              onKeyDown={handleKeyDown}
              className="text-lg font-semibold bg-transparent border-b-2 border-blue-500 outline-none text-gray-800 dark:text-gray-200"
            />
          ) : (
            <>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                {activeWindow.name}
              </h2>
              {isEditing && (
                <button
                  onClick={handleStartRename}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600"
                  title="Rename window"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAddWidget(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Widget
          </button>

          {isEditing && (
            <button
              onClick={() => {
                if (confirm(`Delete window "${activeWindow.name}"?`)) {
                  deleteWindow(activeWindow.id);
                }
              }}
              className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900 text-gray-400 hover:text-red-600"
              title="Delete window"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}

          <button
            onClick={() => setEditing(!isEditing)}
            className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded transition-colors ${
              isEditing
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
            title={isEditing ? 'Done editing' : 'Edit layout'}
          >
            {isEditing ? <Check className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
            {isEditing ? 'Done' : 'Edit'}
          </button>
        </div>
      </div>

      {/* Widget grid */}
      <div className="flex-1 overflow-auto p-4 bg-gray-50 dark:bg-gray-900">
        <WidgetGrid window={activeWindow} />
      </div>

      {/* Add widget modal */}
      {showAddWidget && (
        <AddWidgetModal
          windowId={activeWindow.id}
          onClose={() => { setShowAddWidget(false); setEditing(true); }}
        />
      )}
    </div>
  );
}
